import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Device } from '@shared/models';

export type DeviceFormMode = 'add' | 'edit' | 'view';

export interface DeviceFormControls {
  Name: FormControl<string | null>;
  AuthorizationCode: FormControl<string | null>;
  SerialNumber: FormControl<string | null>;
}

export type DeviceFormGroup = FormGroup<DeviceFormControls>;

function atLeastOneIdentifierValidator(group: AbstractControl): ValidationErrors | null {
  const authorizationCode = group.get('AuthorizationCode')?.value;
  const serialNumber = group.get('SerialNumber')?.value;
  return !authorizationCode && !serialNumber ? { atLeastOneRequired: true } : null;
}

@Injectable()
export class DeviceFormService {
  private readonly fb = inject(NonNullableFormBuilder);

  private readonly mode$$ = new BehaviorSubject<DeviceFormMode>('add');
  readonly mode$ = this.mode$$.asObservable();

  private readonly deviceForm: DeviceFormGroup = this.fb.group<DeviceFormControls>({
    Name: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    AuthorizationCode: this.fb.control<string | null>(null),
    SerialNumber: this.fb.control<string | null>(null),
  });

  constructor() {
    this.setupMutualExclusivity();
  }

  getForm(): DeviceFormGroup {
    return this.deviceForm;
  }

  getMode(): DeviceFormMode {
    return this.mode$$.value;
  }

  initializeForAdd(): void {
    this.mode$$.next('add');
    this.deviceForm.reset({ Name: null, AuthorizationCode: null, SerialNumber: null });
    this.deviceForm.enable();
    this.deviceForm.addValidators(atLeastOneIdentifierValidator);
    this.deviceForm.updateValueAndValidity();
  }

  initializeForView(device: Device): void {
    this.mode$$.next('view');
    this.resetToExisting(device);
    this.deviceForm.disable();
  }

  initializeForEdit(device: Device): void {
    this.mode$$.next('edit');
    this.deviceForm.enable();
    this.resetToExisting(device);
  }

  private resetToExisting(device: Device): void {
    this.deviceForm.removeValidators(atLeastOneIdentifierValidator);
    this.deviceForm.reset({
      Name: device.Name ?? null,
      AuthorizationCode: null,
      SerialNumber: device.SerialNumber ?? null,
    });
    this.deviceForm.updateValueAndValidity();
  }

  private setupMutualExclusivity(): void {
    const authorizationCode = this.deviceForm.controls.AuthorizationCode;
    const serialNumber = this.deviceForm.controls.SerialNumber;

    authorizationCode.valueChanges.subscribe((value) => {
      if (value && serialNumber.enabled) {
        serialNumber.disable({ emitEvent: false });
      } else if (!value && serialNumber.disabled && !serialNumber.value) {
        serialNumber.enable({ emitEvent: false });
      }
    });

    serialNumber.valueChanges.subscribe((value) => {
      if (value && authorizationCode.enabled) {
        authorizationCode.disable({ emitEvent: false });
      } else if (!value && authorizationCode.disabled && !authorizationCode.value) {
        authorizationCode.enable({ emitEvent: false });
      }
    });
  }
}
